
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { useSubscriptions } from '../hooks/useSubscriptions';
import { lumi } from '../lib/lumi';
import {Calculator, Apple, Utensils, Target, Clock, Info, Lock, ShoppingCart, ExternalLink, Star, Zap, Award, TrendingUp, ChevronRight, CheckCircle, Sparkles, Loader, Key, Mail} from 'lucide-react';
import { Link } from 'react-router-dom';

const Nutrition: React.FC = () => {
  const { user } = useAuth();
  const { currentSubscription, fetchUserSubscription } = useSubscriptions();
  const [activeTab, setActiveTab] = useState('conseils');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('homme');
  const [activity, setActivity] = useState('modere');
  const [goal, setGoal] = useState('maintien');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const activationTrackedRef = useRef(false);
  const isPremium = !!currentSubscription || isUnlocked; // Utilisateurs avec abonnement actif ou code d'accès valide ont accès premium

  // États pour le générateur IA
  const [aiPlan, setAiPlan] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [dietType, setDietType] = useState('omnivore');
  const [allergies, setAllergies] = useState('');
  const [preferences, setPreferences] = useState('');

  useEffect(() => {
    if (user?.userId) {
      fetchUserSubscription(user.userId);
    }
  }, [user]);

  useEffect(() => {
    const localUnlock = localStorage.getItem('titan_nutrition_unlocked');
    const exercisesUnlock = localStorage.getItem('titan_exercises_unlocked');
    const masterUnlock = localStorage.getItem('titan_master_unlocked');

    if ((currentSubscription || localUnlock === 'true' || exercisesUnlock === 'true' || masterUnlock === 'true') && !isUnlocked) {
      setIsUnlocked(true);
      if (masterUnlock !== 'true') {
        localStorage.setItem('titan_master_unlocked', 'true');
      }

      if (currentSubscription) {
        toast.success("Accès Premium validé : Nutrition débloquée !", {
          icon: '🔓',
          style: {
            background: '#1f2937',
            color: '#fff',
            border: '1px solid #22c55e'
          }
        });
      }
    }
  }, [currentSubscription, isUnlocked]);

  const handleUnlock = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    setPasswordError(false);
    setErrorMsg('');

    try {
      const functionUrl = 'https://api.lumi.new/v1/functions/p380144077566414848/verifyAccessCode';

      const res = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: password })
      });

      const response = await res.json();

      if (response.valid) {
        setIsUnlocked(true);
        localStorage.setItem('titan_nutrition_unlocked', 'true');
        localStorage.setItem('titan_master_unlocked', 'true');

        if (response.type === 'client') {
          localStorage.setItem('titan_access_type', 'client');
          toast.success("Code client validé ! Nutrition débloquée.", { icon: '🔓' });
        } else {
          localStorage.setItem('titan_access_type', 'admin');
          toast.success("Accès Administrateur validé !", { icon: '👑' });
        }
      } else {
        setPasswordError(true);
        setErrorMsg('Code invalide');
        setTimeout(() => setPasswordError(false), 3000);
      }
    } catch (err) {
      console.error(err);
      setPasswordError(true);
      setErrorMsg('Erreur de vérification');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!user?.email) {
      toast.error("Vous devez être connecté pour renvoyer le code d'accès");
      return;
    }

    setIsResending(true);
    try {
      await lumi.functions.invoke('sendAccessCodeEmail', {
        method: 'POST'
      });
      toast.success(
        "Code d'accès renvoyé ! Vérifiez votre boîte mail.\n\nℹ️ Pensez à vérifier vos spams.",
        { duration: 6000, icon: '✉️' }
      );

      // Track resend_code event (silent)
      try {
        await lumi.entities.user_events.create({
          userId: user.userId!,
          eventType: 'resend_code',
          timestamp: new Date().toISOString(),
          metadata: { page: 'nutrition' },
          creator: user.userId!,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        console.log("resend_code event tracked");
      } catch (trackErr) {
        console.error("Failed to track resend_code (non-critical):", trackErr);
      }
    } catch (err: any) {
      console.error('Resend failed:', err);
      toast.error(
        err?.data?.error || "Erreur lors du renvoi. Contactez le support : bodytitan64@yahoo.com",
        { duration: 5000 }
      );
    } finally {
      setIsResending(false);
    }
  };

  const calculateCalories = () => {
    if (!weight || !height || !age) return 0;

    let bmr = 0;
    if (gender === 'homme') {
      bmr = 88.362 + 13.397 * parseFloat(weight) + 4.799 * parseFloat(height) - 5.677 * parseFloat(age);
    } else {
      bmr = 447.593 + 9.247 * parseFloat(weight) + 3.098 * parseFloat(height) - 4.330 * parseFloat(age);
    }

    const activityMultipliers = {
      sedentaire: 1.2,
      leger: 1.375,
      modere: 1.55,
      intense: 1.725,
      extreme: 1.9
    };

    const goalMultipliers = {
      perte: 0.8,
      maintien: 1,
      prise: 1.2
    };

    return Math.round(bmr * activityMultipliers[activity as keyof typeof activityMultipliers] * goalMultipliers[goal as keyof typeof goalMultipliers]);
  };

  // ... keep existing code (data arrays)

  const nutritionTips = [
  {
    title: "Hydratation Optimale",
    description: "Buvez au minimum 35ml d'eau par kg de poids corporel par jour",
    icon: "💧",
    details: "L'eau représente 60% de notre poids corporel. Une hydratation optimale améliore les performances, la récupération et le métabolisme."
  },
  {
    title: "Protéines de Qualité",
    description: "1.6-2.2g de protéines par kg de poids corporel pour la musculation",
    icon: "🥩",
    details: "Les protéines sont essentielles pour la construction et la réparation musculaire. Variez les sources : viandes, poissons, œufs, légumineuses."
  },
  {
    title: "Timing des Repas",
    description: "Mangez toutes les 3-4 heures pour maintenir un métabolisme actif",
    icon: "⏰",
    details: "Répartir les apports nutritionnels sur la journée optimise l'absorption des nutriments et maintient l'énergie stable."
  },
  {
    title: "Glucides Stratégiques",
    description: "Consommez les glucides autour de vos entraînements",
    icon: "🍠",
    details: "Les glucides fournissent l'énergie nécessaire à l'entraînement et favorisent la récupération musculaire post-effort."
  },
  {
    title: "Lipides Essentiels",
    description: "25-30% de vos calories doivent provenir des bonnes graisses",
    icon: "🥑",
    details: "Les lipides sont cruciaux pour la production hormonale et l'absorption des vitamines liposolubles."
  },
  {
    title: "Micronutriments",
    description: "Privilégiez les aliments colorés et variés",
    icon: "🌈",
    details: "Vitamines et minéraux sont indispensables au bon fonctionnement de l'organisme et à la performance sportive."
  }];


  const mealPlans = [
  {
    title: "Plan Prise de Masse",
    calories: "3000-3500 kcal",
    macros: "P: 220g | G: 450g | L: 90g",
    description: "Pour développer la masse musculaire",
    meals: [
    {
      name: "Petit-déjeuner",
      foods: ["3 œufs entiers + 2 blancs", "100g flocons d'avoine", "1 banane", "30g amandes", "Café/thé"],
      calories: "750 kcal"
    },
    {
      name: "Collation matin",
      foods: ["Shaker protéines (30g)", "1 pomme", "20g noix"],
      calories: "350 kcal"
    },
    {
      name: "Déjeuner",
      foods: ["150g poulet", "100g riz basmati (cru)", "Légumes verts", "1 c.s huile olive"],
      calories: "800 kcal"
    },
    {
      name: "Collation pré-training",
      foods: ["1 banane", "30g amandes", "Café"],
      calories: "300 kcal"
    },
    {
      name: "Post-training",
      foods: ["Shaker protéines (40g)", "1 banane", "Créatine"],
      calories: "250 kcal"
    },
    {
      name: "Dîner",
      foods: ["150g saumon", "200g patate douce", "Salade verte", "Avocat"],
      calories: "750 kcal"
    },
    {
      name: "Collation soir",
      foods: ["Fromage blanc 0%", "30g noix", "1 c.s miel"],
      calories: "300 kcal"
    }]

  },
  {
    title: "Plan Végétarien",
    calories: "2500-2800 kcal",
    macros: "P: 180g | G: 350g | L: 75g",
    description: "Développement musculaire sans viande",
    meals: [
    {
      name: "Petit-déjeuner",
      foods: ["3 œufs bio", "80g flocons d'avoine", "30g beurre de cacahuète", "1 banane", "Lait végétal"],
      calories: "650 kcal"
    },
    {
      name: "Collation matin",
      foods: ["Shaker protéines végétales (30g)", "1 pomme", "30g amandes"],
      calories: "350 kcal"
    },
    {
      name: "Déjeuner",
      foods: ["150g tofu ferme", "100g quinoa (cru)", "Légumes variés", "Avocat", "1 c.s huile olive"],
      calories: "700 kcal"
    },
    {
      name: "Collation pré-training",
      foods: ["100g fromage blanc", "50g granola", "Fruits rouges"],
      calories: "300 kcal"
    },
    {
      name: "Post-training",
      foods: ["Shaker protéines végétales (40g)", "1 banane", "Créatine"],
      calories: "250 kcal"
    },
    {
      name: "Dîner",
      foods: ["150g seitan", "150g patate douce", "Brocolis", "20g noix de cajou"],
      calories: "650 kcal"
    },
    {
      name: "Collation soir",
      foods: ["Skyr nature", "30g graines mélangées", "1 c.s miel"],
      calories: "280 kcal"
    }]

  },
  {
    title: "Plan Sèche",
    calories: "1800-2200 kcal",
    macros: "P: 200g | G: 150g | L: 60g",
    description: "Pour perdre du gras tout en préservant le muscle",
    meals: [
    {
      name: "Petit-déjeuner",
      foods: ["3 blancs d'œufs + 1 jaune", "50g flocons d'avoine", "Fruits rouges", "Thé vert"],
      calories: "350 kcal"
    },
    {
      name: "Collation",
      foods: ["Shaker protéines (25g)", "1 pomme"],
      calories: "200 kcal"
    },
    {
      name: "Déjeuner",
      foods: ["120g blanc de poulet", "80g riz basmati (cru)", "Légumes verts à volonté"],
      calories: "450 kcal"
    },
    {
      name: "Pré-training",
      foods: ["1 banane", "Café noir", "BCAA"],
      calories: "100 kcal"
    },
    {
      name: "Post-training",
      foods: ["Shaker protéines (30g)", "Créatine"],
      calories: "120 kcal"
    },
    {
      name: "Dîner",
      foods: ["150g poisson blanc", "150g légumes verts", "1 c.s huile olive"],
      calories: "400 kcal"
    },
    {
      name: "Collation soir",
      foods: ["Caseine (20g)", "Quelques amandes"],
      calories: "180 kcal"
    }]

  }];


  const supplements = [
  {
    name: "Whey Protéine",
    timing: "Post-training (30min après)",
    dosage: "25-40g par prise",
    benefits: "Récupération musculaire rapide et synthèse protéique",
    priority: "Essentiel",
    description: "Protéine à absorption rapide extraite du lactosérum. Riche en acides aminés essentiels et BCAA.",
    usage: "Mélanger avec de l'eau ou du lait. Peut être prise au réveil ou entre les repas."
  },
  {
    name: "Créatine Monohydrate",
    timing: "Quotidien (moment flexible)",
    dosage: "3-5g par jour",
    benefits: "Augmente la force, la puissance et le volume musculaire",
    priority: "Essentiel",
    description: "Supplément le plus étudié en musculation. Augmente les réserves de phosphocréatine.",
    usage: "Phase de charge optionnelle : 20g/jour pendant 5 jours, puis 3-5g/jour."
  },
  {
    name: "Oméga 3 (EPA/DHA)",
    timing: "Avec les repas",
    dosage: "2-3g par jour",
    benefits: "Anti-inflammatoire, santé cardiovasculaire",
    priority: "Essentiel",
    description: "Acides gras essentiels provenant d'huile de poisson. Puissants anti-inflammatoires naturels.",
    usage: "Prendre avec un repas contenant des graisses pour une meilleure absorption."
  }];

  // VERSION PUBLIQUE (NON DÉBLOQUÉE)
  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl border border-blue-500/30 max-w-md w-full">
          <div className="flex flex-col items-center mb-6">
            <Lock className="w-16 h-16 text-blue-500 mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Accès Nutrition</h2>
            <p className="text-gray-400 text-center">Plans repas, calculateur calories et guide suppléments réservés aux membres Premium</p>
          </div>
          
          <form onSubmit={handleUnlock} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Code d'accès
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Entrez votre code d'accès"
                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {passwordError && (
                <p className="mt-2 text-sm text-red-400">{errorMsg}</p>
              )}
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Key className="w-5 h-5" />
              )}
              {isLoading ? 'Vérification...' : 'Déverrouiller'}
            </button>
          </form>

          {/* Bouton Renvoyer le code */}
          <div className="mt-6 pt-6 border-t border-gray-700">
            <p className="text-sm text-gray-400 text-center mb-3">
              Vous n'avez pas reçu votre code d'accès ?
            </p>
            <button
              onClick={handleResendCode}
              disabled={isResending || !user}
              className="w-full bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Renvoyer le code d'accès
                </>
              )}
            </button>
            {!user && (
              <p className="text-xs text-yellow-400 text-center mt-2">
                ⚠️ Connectez-vous d'abord pour renvoyer le code
              </p>
            )}
          </div>

          {/* Lien vers abonnement */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-400 mb-3">Pas encore membre ?</p>
            <Link
              to="/abonnement"
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-semibold"
            >
              <Zap className="w-4 h-4" />
              Découvrir l'abonnement Premium
            </Link>
          </div>

          {/* Lien vers le guide d'aide */}
          <div className="mt-4 text-center">
            <a
              href="/aide"
              className="text-sm text-blue-400 hover:text-blue-300 underline"
            >
              📘 Guide : Que faire si je n'ai pas reçu mon code ?
            </a>
          </div>
        </div>
      </div>
    );
  }



  // VERSION PREMIUM (DÉBLOQUÉE) - Keep existing unlocked content
  return (
    <div className="min-h-screen bg-gray-900 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12">
          <div className="inline-block bg-green-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
            ✅ ACCÈS PREMIUM ACTIVÉ
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Nutrition <span className="text-green-500">Sportive</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Conseils nutritionnels de ton conseiller pour optimiser vos résultats
          </p>
        </motion.div>

        {/* Navigation Tabs */}
        <div className="flex justify-center mb-12 overflow-x-auto">
          <div className="bg-gray-800 rounded-lg p-1 flex flex-wrap gap-2">
            {[
            { id: 'conseils', label: 'Conseils', icon: Info },
            { id: 'ai-generator', label: 'Plan IA', icon: Sparkles },
            { id: 'calculateur', label: 'Calculateur', icon: Calculator },
            { id: 'plans', label: 'Plans Repas', icon: Utensils },
            { id: 'supplements', label: 'Suppléments', icon: Apple },
            { id: 'recommandations', label: 'Ce que j\'utilise', icon: ShoppingCart }].
            map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex flex-col items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeTab === tab.id ?
                  'bg-blue-600 text-white shadow-lg transform scale-105' :
                  'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'}`
                  }>
                  <Icon className="w-6 h-6" />
                  <span className="text-sm">{tab.label}</span>
                </button>);

            })}
          </div>
        </div>

        {/* Tab Content - Keep all existing content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}>

          {/* AI Generator Tab */}
          {activeTab === 'ai-generator' &&
          <div className="max-w-4xl mx-auto">
              <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-8 text-center mb-8">
                <Sparkles className="w-16 h-16 text-white mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-white mb-3"> Générateur de Plan Nutrition </h2>
                <p className="text-blue-100 text-lg">Obtiens un plan nutritionnel 100% personnalisé 

              </p>
              </motion.div>

              {!aiPlan ?
            <div className="bg-gray-800 rounded-lg p-8">
                  <h3 className="text-xl font-bold text-white mb-6 text-center">📋 Remplis ton profil pour ton plan personnalisé

              </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-gray-300 mb-2">Poids (kg)</label>
                      <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                    placeholder="75" />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Taille (cm)</label>
                      <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                    placeholder="180" />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Âge</label>
                      <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                    placeholder="28" />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Sexe</label>
                      <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none">
                        <option value="homme">Homme</option>
                        <option value="femme">Femme</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Activité</label>
                      <select
                    value={activity}
                    onChange={(e) => setActivity(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none">
                        <option value="sedentaire">Sédentaire</option>
                        <option value="leger">Léger (1-3x/sem)</option>
                        <option value="modere">Modéré (3-5x/sem)</option>
                        <option value="intense">Intense (6-7x/sem)</option>
                        <option value="extreme">Extrême (2x/jour)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Objectif</label>
                      <select
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none">
                        <option value="perte">Perte de poids</option>
                        <option value="maintien">Maintien</option>
                        <option value="prise">Prise de masse</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Type de régime</label>
                      <select
                    value={dietType}
                    onChange={(e) => setDietType(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none">
                        <option value="omnivore">Omnivore</option>
                        <option value="végétarien">Végétarien</option>
                        <option value="végétalien">Végétalien</option>
                        <option value="flexitarien">Flexitarien</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Allergies (optionnel)</label>
                      <input
                    type="text"
                    value={allergies}
                    onChange={(e) => setAllergies(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                    placeholder="Gluten, lactose..." />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-gray-300 mb-2">Préférences alimentaires (optionnel)</label>
                    <textarea
                  value={preferences}
                  onChange={(e) => setPreferences(e.target.value)}
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  rows={3}
                  placeholder="Ex: Je préfère les viandes blanches, j'aime les fruits rouges..." />

                  </div>

                  <button
                onClick={async () => {
                  if (!weight || !height || !age) {
                    toast.error('Veuillez remplir tous les champs obligatoires');
                    return;
                  }

                  setIsGenerating(true);
                  try {
                    const allergyList = allergies ? allergies.split(',').map((a) => a.trim()) : [];
                    const result = await lumi.functions.invoke('generateNutritionPlan', {
                      method: 'POST',
                      body: {
                        weight,
                        height,
                        age,
                        gender,
                        activity,
                        goal,
                        dietType,
                        allergies: allergyList,
                        preferences
                      }
                    });

                    if (result.success) {
                      setAiPlan(result.plan);
                      toast.success('✨ Plan nutrition généré avec succès !', {
                        duration: 4000,
                        icon: '🎉'
                      });
                    } else {
                      throw new Error('Erreur lors de la génération');
                    }
                  } catch (error: any) {
                    console.error('AI Generation error:', error);
                    toast.error(error?.data?.error || 'Erreur lors de la génération du plan');
                  } finally {
                    setIsGenerating(false);
                  }
                }}
                disabled={isGenerating || !weight || !height || !age}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-blue-500 hover:to-purple-500 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3">
                    {isGenerating ?
                <>
                        <Loader className="w-6 h-6 animate-spin" />
                        Génération en cours...
                      </> :

                <>
                        <Sparkles className="w-6 h-6" />
                        Générer Mon Plan Nutrition IA
                      </>
                }
                  </button>

                  <p className="text-gray-400 text-sm text-center mt-4">
                    ⚡ Génération par IA en ~10 secondes
                  </p>
                </div> :

            <div className="space-y-6">
                  <div className="bg-gray-800 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-blue-400" />
                        Ton Plan Nutrition Personnalisé
                      </h3>
                      <button
                    onClick={() => {
                      setAiPlan('');
                      toast.success('Prêt pour un nouveau plan !');
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-all text-sm font-semibold">
                        Générer un nouveau plan
                      </button>
                    </div>
                    
                    <div className="bg-gray-900 rounded-lg p-6 max-h-[600px] overflow-y-auto">
                      <div className="prose prose-invert max-w-none">
                        <pre className="whitespace-pre-wrap text-gray-300 font-sans text-sm leading-relaxed">
                          {aiPlan}
                        </pre>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-6 text-center">
                    <p className="text-white font-semibold mb-2">💪 Plan généré avec succès !</p>
                    <p className="text-blue-100 text-sm">
                      Enregistre ce plan ou génère-en un nouveau avec des paramètres différents
                    </p>
                  </div>
                </div>
            }
            </div>
          }

          {/* Conseils Tab */}
          {activeTab === 'conseils' &&
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {nutritionTips.map((tip, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-gray-800 rounded-lg p-6 hover:bg-gray-700 transition-colors">
                  <div className="text-4xl mb-4">{tip.icon}</div>
                  <h3 className="text-xl font-bold text-white mb-3">{tip.title}</h3>
                  <p className="text-blue-400 font-semibold mb-3">{tip.description}</p>
                  <p className="text-gray-300 text-sm">{tip.details}</p>
                </motion.div>
            )}
            </div>
          }

          {/* Calculateur Tab */}
          {activeTab === 'calculateur' &&
          <div className="max-w-2xl mx-auto">
              <div className="bg-gray-800 rounded-lg p-8 relative">
                <h2 className="font-bold text-white mb-6 text-center text-xl">
                  🔥 Calcule tes calories & macros
                  <br />
                  Basé sur ton sexe, ton poids, ton objectif et ton niveau d'activité
                  <br />
                  🎯 Objectif personnalisé • Simple • Sans prise de tête
                </h2>
                
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 ${!isPremium ? 'blur-sm pointer-events-none' : ''}`}>
                  <div>
                    <label className="block text-gray-300 mb-2">Poids (kg)</label>
                    <input type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  placeholder="70" />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Taille (cm)</label>
                    <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                    placeholder="175" />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Âge</label>
                    <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                    placeholder="25" />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Sexe</label>
                    <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none">
                      <option value="homme">Homme</option>
                      <option value="femme">Femme</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Activité</label>
                    <select
                    value={activity}
                    onChange={(e) => setActivity(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none">
                      <option value="sedentaire">Sédentaire</option>
                      <option value="leger">Léger (1-3x/sem)</option>
                      <option value="modere">Modéré (3-5x/sem)</option>
                      <option value="intense">Intense (6-7x/sem)</option>
                      <option value="extreme">Extrême (2x/jour)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Objectif</label>
                    <select
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none">
                      <option value="perte">Perte de poids</option>
                      <option value="maintien">Maintien</option>
                      <option value="prise">Prise de masse</option>
                    </select>
                  </div>
                </div>

                {weight && height && age &&
              <div className={`bg-gradient-to-r from-blue-500 to-blue-700 rounded-lg p-6 text-center ${!isPremium ? 'blur-sm' : ''}`}>
                    <h3 className="text-2xl font-bold text-white mb-2">
                      Vos besoins caloriques
                    </h3>
                    <p className="text-4xl font-bold text-white mb-2">
                      {calculateCalories()} kcal/jour
                    </p>
                    <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
                      <div className="bg-white bg-opacity-20 rounded p-3">
                        <p className="font-semibold">Protéines</p>
                        <p>{Math.round(calculateCalories() * 0.25 / 4)}g</p>
                      </div>
                      <div className="bg-white bg-opacity-20 rounded p-3">
                        <p className="font-semibold">Glucides</p>
                        <p>{Math.round(calculateCalories() * 0.45 / 4)}g</p>
                      </div>
                      <div className="bg-white bg-opacity-20 rounded p-3">
                        <p className="font-semibold">Lipides</p>
                        <p>{Math.round(calculateCalories() * 0.30 / 9)}g</p>
                      </div>
                    </div>
                  </div>
              }

              {!isPremium &&
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-70 rounded-lg">
                  <Lock className="w-12 h-12 text-blue-400 mb-3" />
                  <p className="text-white font-bold text-lg mb-1 text-center px-4">Tu as vu l'aperçu</p>
                  <p className="text-blue-200 text-sm mb-4 text-center px-4">Débloque le calculateur complet</p>
                  <Link
                  to="/abonnement"
                  className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-3 rounded-lg font-bold hover:from-blue-500 hover:to-blue-400 transition-all transform hover:scale-105 shadow-xl flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Débloquer l'accès complet
                  </Link>
                </div>
              }
              </div>
            </div>
          }

          {/* Plans Tab - Freemium: aperçu gratuit, détails verrouillés */}
          {activeTab === 'plans' &&
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {mealPlans.map((plan, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="bg-gray-800 rounded-lg p-6 relative">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.title}</h3>
                    <p className="text-green-400 font-semibold text-lg">{plan.calories}</p>
                    <p className="text-blue-400 font-medium text-sm mb-2">{plan.macros}</p>
                    <p className="text-gray-300">{plan.description}</p>
                  </div>
                  <div className={`space-y-4 ${!isPremium ? 'blur-sm pointer-events-none' : ''}`}>
                    {plan.meals.map((meal, mealIndex) =>
                <div key={mealIndex} className="bg-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-bold text-white">{meal.name}</h4>
                          <span className="text-blue-400 font-semibold text-sm">{meal.calories}</span>
                        </div>
                        <ul className="text-gray-300 text-sm space-y-1">
                          {meal.foods.map((food, foodIndex) =>
                    <li key={foodIndex} className="flex items-center">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                              {food}
                            </li>
                    )}
                        </ul>
                      </div>
                )}
                  </div>
                  {!isPremium &&
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-70 rounded-lg">
                      <Lock className="w-12 h-12 text-blue-400 mb-3" />
                      <p className="text-white font-bold text-lg mb-1 text-center px-4">Tu as vu l'aperçu</p>
                      <p className="text-blue-200 text-sm mb-4 text-center px-4">Débloque le plan complet avec tous les repas</p>
                      <Link
                  to="/abonnement"
                  className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-3 rounded-lg font-bold hover:from-blue-500 hover:to-blue-400 transition-all transform hover:scale-105 shadow-xl flex items-center gap-2">
                        <Zap className="w-5 h-5" />
                        Débloquer l'accès complet
                      </Link>
                    </div>
              }
                </motion.div>
            )}
            </div>
          }

          {/* Supplements Tab */}
          {activeTab === 'supplements' &&
          <div className="relative">
              <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 ${!isPremium ? 'blur-sm pointer-events-none' : ''}`}>
              {supplements.map((supplement, index) =>
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-gray-800 rounded-lg p-6 hover:bg-gray-700 transition-all border-2 border-gray-700 hover:border-blue-500">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">{supplement.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  supplement.priority === 'Essentiel' ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'}`
                  }>
                      {supplement.priority}
                    </span>
                  </div>
                  <p className="text-gray-300 text-sm mb-4">{supplement.description}</p>
                  <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-2">
                      <Clock className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-gray-400 text-xs">Timing</p>
                        <p className="text-white text-sm font-semibold">{supplement.timing}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Target className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-gray-400 text-xs">Dosage</p>
                        <p className="text-white text-sm font-semibold">{supplement.dosage}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-3 mb-4">
                    <p className="text-blue-400 text-sm font-semibold mb-1">Bénéfices</p>
                    <p className="text-gray-300 text-sm">{supplement.benefits}</p>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-3">
                    <p className="text-green-400 text-sm font-semibold mb-1">Utilisation</p>
                    <p className="text-gray-300 text-sm">{supplement.usage}</p>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
          }

          {/* Recommandations Tab */}
          {activeTab === 'recommandations' &&
          <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-8 text-center mb-8">
                <ShoppingCart className="w-16 h-16 text-white mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-white mb-3">Ma Stack Complète</h2>
                <p className="text-blue-100 text-lg mb-6">
                  Découvre tous les produits et suppléments que j'utilise personnellement pour mes résultats
                </p>
                <Link
                to="/ressources"
                className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl">
                  <ExternalLink className="w-6 h-6" />
                  Voir Toute Ma Stack
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {[
              { title: 'Suppléments', desc: '10+ produits essentiels', icon: '💊' },
              { title: 'Équipements', desc: 'Matos recommandé', icon: '🏋️' },
              { title: 'Nutrition', desc: 'Aliments & recettes', icon: '🍽️' }].
              map((item, idx) =>
              <div key={idx} className="bg-gray-800 rounded-lg p-6 text-center">
                    <div className="text-4xl mb-3">{item.icon}</div>
                    <h3 className="text-white font-bold mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
              )}
              </div>
            </div>
          }
        </motion.div>
      </div>
    </div>);

};

export default Nutrition;